import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { Badge } from "@shared/ui/badge";
import { Minus } from "lucide-react";
import { FormService, FormField } from "@shared/services/FormServices";
import { DynamicForm } from "@shared/components/DynamicForm";
import { toast } from "@shared/hooks/use-toast";
import { Button } from "@shared/ui/button";
import { ValueLabel } from "@features/ax/shared/ValueLabel";
import { BUTTON_FIELD_CLASSES } from "@shared/utils/constants";

interface ReduceTrashQuantityModalProps {
  isOpen: boolean;
  onClose: () => void;
  connection: { id: string; connectionDate: string; quantity: number } | null;
  onReduce: (connId: string, newQuantity: number, reductionDate?: string) => void;
}

export const ReduceTrashQuantityModal: React.FC<
  ReduceTrashQuantityModalProps
> = ({ isOpen, onClose, connection, onReduce }) => {
  if (!connection) return null;

  const maxAllowed = Math.max(0, connection.quantity - 1);

  const fields: FormField[] = [
    {
      name: "reductionDate",
      label: "Reduction Date",
      type: "datepicker",
      placeholder: "dd-mm-yyyy",
      required: false,
      className: "col-span-2",
    },
    {
      name: "newQuantity",
      label: "Reduce to (new quantity)",
      type: "number",
      required: true,
      min: 0,
      max: maxAllowed,
      defaultValue: 0,
      helperText: "Enter 0 to remove all units entirely.",
      className: "col-span-2",
    },
  ];

  const form = FormService.createForm({
    fields,
    defaultValues: {
      reductionDate: undefined,
      newQuantity: 0,
    },
  });

  const currentNewQuantity = form.watch("newQuantity");
  const parsedQty = Number(currentNewQuantity);
  const isReduceDisabled = isNaN(parsedQty) || parsedQty >= connection.quantity || currentNewQuantity === "";

  const renderCustomFooter = () => (
    <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 w-full col-span-full">
      <Button variant="outline" onClick={onClose} type="button">
        Cancel
      </Button>
      <Button 
        type="submit" 
        className="bg-red-600 hover:bg-red-700 text-white shadow-sm border-0"
        disabled={isReduceDisabled}
      >
        Reduce Quantity
      </Button>
    </div>
  );

  const handleSubmit = (data: any) => {
    const newQty = Number(data.newQuantity);
    if (isNaN(newQty) || newQty < 0) {
      toast({
        variant: "destructive",
        description: "Please enter a valid quantity (minimum 0).",
      });
      return;
    }
    if (newQty >= connection.quantity) {
      toast({
        variant: "destructive",
        description: `New quantity must be less than current quantity (${connection.quantity}).`,
      });
      return;
    }

    onReduce(connection.id, newQty, data.reductionDate);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[480px] flex flex-col p-0 overflow-hidden rounded-2xl bg-white shadow-xl border border-gray-100">
        <DialogHeader className="px-6 py-4 border-b border-gray-100 flex flex-row items-center justify-between">
          <div className="flex items-center gap-2.5">
            <Minus className="w-5 h-5 text-red-500 stroke-[3]" />
            <DialogTitle className="text-base font-semibold text-gray-900">
              Reduce Trash Quantity
            </DialogTitle>
            <Badge
              variant="secondary"
              className="bg-blue-50 text-blue-700 border border-blue-200 text-xs font-medium rounded-full px-2.5 py-0.5"
            >
              Quantity Based
            </Badge>
          </div>
        </DialogHeader>

        <div className="px-6 py-4 space-y-4">
          {/* Display-only Info Panel using ValueLabel */}
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 grid grid-cols-2 gap-4">
            <ValueLabel
              label="Connection Date"
              value={connection.connectionDate}
            />
            <ValueLabel
              label="Current Quantity"
              value={`${connection.quantity} ${connection.quantity === 1 ? "unit" : "units"}`}
            />
          </div>

          <DynamicForm
            form={form}
            fields={fields}
            onSubmit={handleSubmit}
            gridColumns={2}
            config={{
              layout: "vertical",
              showSubmitButton: false,
              showResetButton: false,
              classes: BUTTON_FIELD_CLASSES,
            }}
            renderCustomFooter={renderCustomFooter}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};
